import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { OrgUnitCountsResponse, OrgUnitDto } from '@pams/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/queryKeys';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  OFFICER AND NODAL COUNTS PER ORG UNIT — [amended P1b] ONE REQUEST, ALWAYS EXACT.
 *
 *  What this replaced is worth recording, because the shape of the old problem is why
 *  the endpoint exists. P1a.2 had no counts anywhere in the API, so this hook derived
 *  them: one probe for the officer total, then either a full census (a page of
 *  `/officers` per hundred officers, rolled up through `ancestors`) or — above 1 500
 *  officers — a degraded mode in which the subtree column was reported as `undefined`
 *  and DISAPPEARED from the screen. Correct, and useless at the scale the product is
 *  for: one real commissionerate crosses that line on its first upload.
 *
 *  `GET /org-units/counts` does the `$group` and the roll-up server-side, so the cost
 *  now scales with the ORG TREE rather than with the officer count, and there is one
 *  code path instead of three.
 *
 *  ─── Two things that follow, and both are improvements you can lose by accident ───
 *
 *   • `orphanedOfficers` is now STRUCTURALLY zero in the usual case, and non-zero only
 *     when the tree payload and the counts payload disagree about which units exist —
 *     typically a unit deactivated between the two fetches. It is kept, and surfaced,
 *     rather than deleted: "the numbers do not add up" must remain sayable.
 *
 *   • The counts key lives under `queryKeys.orgUnits`, so the officer flows'
 *     `invalidateQueries({ queryKey: queryKeys.orgUnits.all })` already covers it. That
 *     call was already there for the tree payload; it is now load-bearing for counts
 *     too, so do not narrow it to `queryKeys.orgUnits.trees()`.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

export type OrgUnitCounts = {
  officersOwn: number;
  /** INCLUSIVE of the unit itself — the API's descendant count plus its own. */
  officersSubtree: number;
  nodalsOwn: number;
  nodalsSubtree: number;
};

export type UseOrgUnitCounts = {
  /**
   * `undefined` means "this unit is not in the counts payload" — a partial or errored
   * response, or a tree/counts skew. Distinct from a unit whose count is genuinely
   * zero, which returns zeroes. `CountCell` renders the two differently and that is
   * the entire reason this is not `OrgUnitCounts` with optional members.
   */
  countsFor: (unitId: string) => OrgUnitCounts | undefined;
  isLoading: boolean;
  /** Officers counted against a unit the tree payload does not contain. */
  orphanedOfficers: number;
};

export const useOrgUnitCounts = ({
  units,
  enabled = true,
}: {
  /** The tree as rendered. Used to detect skew against the counts payload. */
  units: readonly OrgUnitDto[];
  enabled?: boolean;
}): UseOrgUnitCounts => {
  const params = { officerStatus: 'active' as const };

  const counts = useQuery({
    queryKey: queryKeys.orgUnits.counts(params),
    queryFn: ({ signal }) => api.get<OrgUnitCountsResponse>('/org-units/counts', { query: params, signal }),
    enabled,
    staleTime: 60_000,
  });

  const { byUnit, orphaned } = useMemo(() => {
    const map = new Map<string, OrgUnitCounts>();
    const items = counts.data?.items ?? [];
    const unitIds = new Set(units.map((unit) => unit.id));

    let orphanedOfficers = 0;

    for (const row of items) {
      if (!unitIds.has(row.orgUnitId)) {
        // The counts payload knows about a unit this tree does not render — a unit
        // deactivated while the screen was open, most often. Its officers are real and
        // are reported separately rather than silently dropped from the totals.
        orphanedOfficers += row.officerCount;
        continue;
      }
      map.set(row.orgUnitId, {
        officersOwn: row.officerCount,
        // The API's descendant counts EXCLUDE the unit itself; every screen wants the
        // inclusive number, so the addition happens once, here.
        officersSubtree: row.officerCount + row.descendantOfficerCount,
        nodalsOwn: row.nodalCount,
        nodalsSubtree: row.nodalCount + row.descendantNodalCount,
      });
    }

    return { byUnit: map, orphaned: orphanedOfficers };
  }, [counts.data, units]);

  return {
    countsFor: (unitId: string) => byUnit.get(unitId),
    isLoading: counts.isLoading,
    orphanedOfficers: orphaned,
  };
};
